import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiImage } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', shortDescription: '', technologies: '', githubUrl: '', liveDemoUrl: '', status: 0, isFeatured: false });

  const loadProjects = async () => {
    try {
      const res = await adminAPI.getProjects();
      if (res.success) setProjects(res.data);
    } catch (err) { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProjects(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', shortDescription: '', technologies: '', githubUrl: '', liveDemoUrl: '', status: 0, isFeatured: false });
    setShowModal(true);
  };

  const openEdit = (project) => {
    setEditing(project);
    setForm({
      title: project.title,
      description: project.description,
      shortDescription: project.shortDescription || '',
      technologies: project.technologies,
      githubUrl: project.githubUrl || '',
      liveDemoUrl: project.liveDemoUrl || '',
      status: ['Completed', 'InProgress', 'OnHold', 'Archived'].indexOf(project.status),
      isFeatured: project.isFeatured,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await adminAPI.updateProject(editing.id, form);
        toast.success('Project updated');
      } else {
        await adminAPI.createProject(form);
        toast.success('Project created');
      }
      setShowModal(false);
      loadProjects();
    } catch (err) { toast.error('Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await adminAPI.deleteProject(id);
      toast.success('Project deleted');
      loadProjects();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleThumbnailUpload = async (projectId, file) => {
    try {
      await adminAPI.uploadThumbnail(projectId, file);
      toast.success('Thumbnail uploaded');
      loadProjects();
    } catch (err) { toast.error('Upload failed'); }
  };

  const filtered = projects.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.technologies?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Projects | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <div className="search-box">
          <FiSearch />
          <input type="text" placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> New Project</button>
      </div>

      <div className="manage-table">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Technologies</th>
              <th>Status</th>
              <th>Views</th>
              <th>Featured</th>
              <th>Thumbnail</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id}>
                <td><strong>{p.title}</strong></td>
                <td><div className="tech-stack">{p.technologies?.split(',').map(t => <span key={t} className="mini-badge">{t.trim()}</span>)}</div></td>
                <td><span className={`status-pill ${p.status?.toLowerCase()}`}>{p.status}</span></td>
                <td>{p.viewCount}</td>
                <td>{p.isFeatured ? 'Yes' : 'No'}</td>
                <td>
                  <label className="upload-btn-sm">
                    <FiImage />
                    <input type="file" hidden accept="image/*" onChange={e => e.target.files[0] && handleThumbnailUpload(p.id, e.target.files[0])} />
                  </label>
                </td>
                <td>
                  <div className="action-btns">
                    <button className="btn-icon edit" onClick={() => openEdit(p)}><FiEdit2 /></button>
                    <button className="btn-icon delete" onClick={() => handleDelete(p.id)}><FiTrash2 /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Project' : 'New Project'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Title</label><input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required /></div>
              <div className="form-group"><label>Short Description</label><input value={form.shortDescription} onChange={e => setForm({...form, shortDescription: e.target.value})} /></div>
              <div className="form-group"><label>Description</label><textarea rows="4" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required /></div>
              <div className="form-group"><label>Technologies (comma separated)</label><input value={form.technologies} onChange={e => setForm({...form, technologies: e.target.value})} required /></div>
              <div className="form-row">
                <div className="form-group"><label>GitHub URL</label><input value={form.githubUrl} onChange={e => setForm({...form, githubUrl: e.target.value})} /></div>
                <div className="form-group"><label>Live Demo URL</label><input value={form.liveDemoUrl} onChange={e => setForm({...form, liveDemoUrl: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: parseInt(e.target.value)})}>
                    <option value={0}>Completed</option>
                    <option value={1}>In Progress</option>
                    <option value={2}>On Hold</option>
                    <option value={3}>Archived</option>
                  </select>
                </div>
                <div className="form-group">
                  <label><input type="checkbox" checked={form.isFeatured} onChange={e => setForm({...form, isFeatured: e.target.checked})} /> Featured</label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProjects;
